/**
 * Support page — publicly reachable from /support. Required by Apple
 * App Store submission (they check the Support URL returns a 200
 * with a real contact channel).
 *
 * Provides three parallel paths to help: an email link that opens
 * the user's mail client, a lightweight in-page contact form that
 * POSTs to `/api/support/message` (fire-and-forget — the endpoint
 * stores the ticket in Mongo and the operator sees it in the admin
 * panel), and a FAQ section that answers the questions that
 * repeatedly show up in the reference apps' 1-star reviews.
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import LegalPage from "./LegalPage";
import useContactInfo from "./useContactInfo";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Mail, MessageSquareText, LifeBuoy, CheckCircle2, AlertCircle } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function Support() {
  const { contactEmail } = useContactInfo();
  const [form, setForm] = useState({
    name: "", email: "", subject: "", message: "", activation_key: "",
  });
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState(null); // { ok, msg }

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.subject || !form.message) {
      setStatus({ ok: false, msg: "Please fill in email, subject and message." });
      return;
    }
    setSending(true);
    setStatus(null);
    try {
      const r = await fetch(`${BACKEND}/api/support/message`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        setStatus({ ok: false, msg: body?.detail || `Server returned ${r.status}. Please try again or email us directly.` });
      } else {
        setStatus({ ok: true, msg: "Message received — we'll reply within one working day." });
        setForm({ name: "", email: "", subject: "", message: "", activation_key: "" });
      }
    } catch (err) {
      setStatus({ ok: false, msg: `Could not reach the server (${err?.message || "network error"}). Please email us directly at ${contactEmail}.` });
    } finally {
      setSending(false);
    }
  };

  return (
    <LegalPage testId="support-page" title="Support" updated="February 9, 2026">
      <p>
        We&rsquo;re a small team and we reply personally to every
        message. Pick the channel that suits you and we&rsquo;ll get back
        to you within <strong>one working day</strong> (usually much sooner).
      </p>

      {/* --- Three contact channels --- */}
      <div className="not-prose mt-8 grid gap-4 md:grid-cols-3" data-testid="support-channels">
        <a
          href={`mailto:${contactEmail}`}
          className="group rounded-2xl border border-white/10 bg-neutral-950/70 p-5 transition-colors hover:border-emerald-400/40 hover:bg-neutral-900/80"
          data-testid="support-email-card"
        >
          <Mail className="mb-3 h-6 w-6 text-emerald-400" />
          <div className="text-base font-semibold text-white">Email us</div>
          <div className="mt-1 break-all text-sm text-emerald-400 group-hover:underline">
            {contactEmail}
          </div>
          <div className="mt-2 text-xs text-neutral-400">Opens your mail client with our address prefilled.</div>
        </a>

        <div
          className="rounded-2xl border border-white/10 bg-neutral-950/70 p-5"
          data-testid="support-form-card"
        >
          <MessageSquareText className="mb-3 h-6 w-6 text-emerald-400" />
          <div className="text-base font-semibold text-white">Send a ticket</div>
          <div className="mt-1 text-sm text-neutral-300">Use the form below — you don&rsquo;t need an account.</div>
          <div className="mt-2 text-xs text-neutral-400">We reply from {contactEmail}, so please whitelist that address.</div>
        </div>

        <a
          href="/trial"
          className="group rounded-2xl border border-white/10 bg-neutral-950/70 p-5 transition-colors hover:border-emerald-400/40 hover:bg-neutral-900/80"
          data-testid="support-trial-card"
        >
          <LifeBuoy className="mb-3 h-6 w-6 text-emerald-400" />
          <div className="text-base font-semibold text-white">Not activated yet?</div>
          <div className="mt-1 text-sm text-emerald-400 group-hover:underline">Try the free 5-day trial &rarr;</div>
          <div className="mt-2 text-xs text-neutral-400">No credit card. One key per device.</div>
        </a>
      </div>

      {/* --- Contact form --- */}
      <h2>Send us a ticket</h2>
      <form onSubmit={submit} className="not-prose mt-4 space-y-4" data-testid="support-form">
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <Label className="text-xs uppercase tracking-widest text-neutral-400">Your name (optional)</Label>
            <Input
              value={form.name}
              onChange={set("name")}
              placeholder="Jane Naidoo"
              className="mt-2 border-white/10 bg-black text-white placeholder:text-neutral-600"
              data-testid="support-name"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-neutral-400">Your email <span className="text-red-400">*</span></Label>
            <Input
              type="email"
              value={form.email}
              onChange={set("email")}
              placeholder="you@example.com"
              className="mt-2 border-white/10 bg-black text-white placeholder:text-neutral-600"
              required
              data-testid="support-email"
            />
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <Label className="text-xs uppercase tracking-widest text-neutral-400">Activation key (optional)</Label>
            <Input
              value={form.activation_key}
              onChange={set("activation_key")}
              placeholder="ABCD1234EFGH"
              className="mt-2 border-white/10 bg-black font-mono uppercase text-white placeholder:text-neutral-600"
              maxLength={16}
              data-testid="support-key"
            />
            <p className="mt-1 text-[11px] text-neutral-500">
              Including this speeds up your reply — we can look up your device before responding.
            </p>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-neutral-400">Subject <span className="text-red-400">*</span></Label>
            <Input
              value={form.subject}
              onChange={set("subject")}
              placeholder="Can't activate my key"
              className="mt-2 border-white/10 bg-black text-white placeholder:text-neutral-600"
              required
              data-testid="support-subject"
            />
          </div>
        </div>

        <div>
          <Label className="text-xs uppercase tracking-widest text-neutral-400">Message <span className="text-red-400">*</span></Label>
          <Textarea
            value={form.message}
            onChange={set("message")}
            placeholder="Tell us what's happening. If you can, include your device model, the app version, and any error message you saw."
            rows={6}
            className="mt-2 border-white/10 bg-black text-white placeholder:text-neutral-600"
            required
            data-testid="support-message"
          />
        </div>

        {status && (
          <div
            className={
              status.ok
                ? "flex items-start gap-3 rounded-xl border border-emerald-400/40 bg-emerald-500/10 p-4 text-sm text-emerald-50"
                : "flex items-start gap-3 rounded-xl border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-50"
            }
            data-testid="support-status"
          >
            {status.ok
              ? <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none text-emerald-300" />
              : <AlertCircle className="mt-0.5 h-4 w-4 flex-none text-red-300" />}
            <span>{status.msg}</span>
          </div>
        )}

        <Button
          type="submit"
          disabled={sending}
          className="rounded-full bg-emerald-500 px-8 py-6 text-base font-semibold text-black hover:bg-emerald-400 disabled:opacity-60"
          data-testid="support-submit"
        >
          {sending ? "Sending…" : "Send message"}
        </Button>
      </form>

      {/* --- FAQ --- */}
      <h2>Common questions</h2>

      <h3>&ldquo;I paid but my key hasn&rsquo;t arrived.&rdquo;</h3>
      <p>
        Payments settle when the payment provider confirms the transaction
        back to our server. That normally happens within seconds of the
        &ldquo;Payment received&rdquo; screen but can take up to two hours
        during busy periods. If it&rsquo;s been longer than that, email us
        the payment reference from the success screen and we&rsquo;ll
        reconcile it manually.
      </p>

      <h3>&ldquo;How do I use my playlist on my TV?&rdquo;</h3>
      <p>
        Install the ZA Player Pro app on your TV, launch it, and it will
        show a 12-character MAC address. From your phone or computer, go
        to <a href="/playlist">zaplayerpro.club/playlist</a>, enter your
        activation key and paste in your M3U URL or Xtream credentials.
        On next launch of the app on the TV, everything appears.
      </p>

      <h3>&ldquo;Which devices do you support?&rdquo;</h3>
      <p>
        iPhone, iPad, Apple TV, Windows, macOS (via the iOS app on Apple
        Silicon), Android phones and tablets, Android TV / Google TV,
        Samsung Smart TV (Tizen), LG Smart TV (webOS), Roku, and Hisense /
        Toshiba / JVC (VIDAA).
      </p>

      <h3>&ldquo;Where does the app get its content?&rdquo;</h3>
      <p>
        From wherever you tell it to. ZA Player Pro is a player only —
        <strong> we don&rsquo;t supply any media</strong>. You bring your
        own m3u playlist or Xtream login from a service you already
        subscribe to.
      </p>

      <h3>&ldquo;Do you have a refund policy?&rdquo;</h3>
      <p>
        Yes — see our{" "}
        <Link to="/terms">Terms of Service</Link>. In short: refunds are
        available under the South African Consumer Protection Act cooling
        off period. In-app purchases made through Apple StoreKit are
        refunded directly by Apple.
      </p>

      <h3>&ldquo;How do I move my key to a new device?&rdquo;</h3>
      <p>
        Reseller and admin operators can release a device on your behalf
        — email <a href={`mailto:${contactEmail}`}>{contactEmail}</a>{" "}
        with your activation key and the MAC address of the new device.
      </p>
    </LegalPage>
  );
}
